import { api_url } from "@/app/api";
import { ATTACHMENT_DEFAULTS, HTTP_STATUS, UPLOAD_PURPOSE } from "@/app/lib/constants";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import { ATTACHMENT_PIPELINE_STATUS, METHOD_TYPE } from "@/app/lib/enums";

export type ChatAttachmentUploadResult = {
   id: string;
   extractedText: string | null;
};

export type ChatAttachmentUploadFailure = {
   error:
      | "tooLarge"
      | "audioTooLong"
      | "audioDurationUnverified"
      | "attachmentPolicyUnavailable"
      | "blockedTestSignature"
      | "contentInspectionUnavailable"
      | "contentTypeMismatch"
      | "processingFailed"
      | "processingTimedOut"
      | "processingUnavailable"
      | "unsafeFileStructure"
      | "unsupportedAttachmentType"
      | "uploadFailed";
};

export type ChatAttachmentProgressStatus =
   | "uploading"
   | "queued"
   | "scanning"
   | "validating"
   | "extracting";

type UploadProgress = (status: ChatAttachmentProgressStatus, fileId?: string) => void;

type AttachmentStatusResponse = {
   pipelineStatus?: string;
   failureCode?: string;
   extractedText?: string;
};

const UPLOAD_FAILURE_BY_REASON: Record<string, ChatAttachmentUploadFailure["error"]> = {
   "audio-too-long": "audioTooLong",
   "audio-duration-unverified": "audioDurationUnverified",
   "chat-policy-unavailable": "attachmentPolicyUnavailable",
   "chat-format-disabled": "unsupportedAttachmentType",
   "chat-size-limit-changed": "tooLarge",
   "content-type-mismatch": "contentTypeMismatch",
   "eicar-test-signature": "blockedTestSignature",
   "content-inspection-unavailable": "contentInspectionUnavailable",
   "processing-failed": "processingFailed",
   "processing-unavailable": "processingUnavailable",
   "queue-unavailable": "processingUnavailable",
   "unsafe-file-structure": "unsafeFileStructure"
};

const uploadFailureFor = (reason?: string): ChatAttachmentUploadFailure => ({
   error: (reason && UPLOAD_FAILURE_BY_REASON[reason]) || "uploadFailed"
});

const readResponseFailure = async (
   response: Response
): Promise<ChatAttachmentUploadFailure> => {
   const payload = await response.json().catch(() => null);
   if (typeof payload?.reason === "string") {
      return uploadFailureFor(payload.reason);
   }
   return response.status === HTTP_STATUS.SERVICE_UNAVAILABLE
      ? { error: "processingUnavailable" }
      : { error: "uploadFailed" };
};

const waitForNextStatus = () =>
   new Promise((resolve) =>
      setTimeout(resolve, ATTACHMENT_DEFAULTS.STATUS_POLL_INTERVAL_MS)
   );

const pollAttachmentStatus = async (
   fileId: string,
   onProgress: UploadProgress,
   includeExtractedText: boolean
): Promise<ChatAttachmentUploadResult | ChatAttachmentUploadFailure> => {
   const deadline = Date.now() + ATTACHMENT_DEFAULTS.STATUS_POLL_TIMEOUT_MS;
   const baseStatusUrl = api_url.chat_attachment_status_url.replace(
      "{id}",
      encodeURIComponent(fileId)
   );
   const statusUrl = includeExtractedText
      ? `${baseStatusUrl}?includeExtractedText=true`
      : baseStatusUrl;

   while (Date.now() < deadline) {
      try {
         const response = await fetchWithAuth(statusUrl, {
            method: METHOD_TYPE.GET
         });
         if (!response.ok) {
            return readResponseFailure(response);
         }
         const status = (await response.json()) as AttachmentStatusResponse;
         switch (status.pipelineStatus) {
            case ATTACHMENT_PIPELINE_STATUS.READY:
               return {
                  id: fileId,
                  extractedText: status.extractedText ?? null
               };
            case ATTACHMENT_PIPELINE_STATUS.REJECTED:
            case ATTACHMENT_PIPELINE_STATUS.FAILED:
               return uploadFailureFor(status.failureCode);
            case ATTACHMENT_PIPELINE_STATUS.SCANNING:
            case ATTACHMENT_PIPELINE_STATUS.VALIDATING:
            case ATTACHMENT_PIPELINE_STATUS.EXTRACTING:
            case ATTACHMENT_PIPELINE_STATUS.QUEUED:
               onProgress(status.pipelineStatus, fileId);
               break;
         }
      } catch {
         await waitForNextStatus();
         continue;
      }
      await waitForNextStatus();
   }

   return { error: "processingTimedOut" };
};

const uploadViaProxy = async (
   file: File,
   onProgress: UploadProgress,
   includeExtractedText: boolean
): Promise<ChatAttachmentUploadResult | ChatAttachmentUploadFailure | null> => {
   const body = new FormData();
   body.append("file", file);
   body.append("purpose", UPLOAD_PURPOSE.CHAT_ATTACHMENT);

   const response = await fetchWithAuth(api_url.upload_url, {
      method: METHOD_TYPE.POST,
      body
   });
   if (!response.ok) {
      if (response.status === HTTP_STATUS.PAYLOAD_TOO_LARGE) {
         return { error: "tooLarge" };
      }
      return readResponseFailure(response);
   }

   const payload = await response.json();
   const fileId = payload?.data?.id;
   if (!fileId) return null;

   onProgress("queued", fileId);
   return pollAttachmentStatus(fileId, onProgress, includeExtractedText);
};

const uploadDirect = async (
   file: File,
   onProgress: UploadProgress,
   includeExtractedText: boolean
): Promise<ChatAttachmentUploadResult | ChatAttachmentUploadFailure | null | "proxy"> => {
   const initResponse = await fetchWithAuth(api_url.chat_attachment_init_url, {
      method: METHOD_TYPE.POST,
      body: JSON.stringify({
         filename: file.name,
         mimetype: file.type,
         size: file.size
      })
   });
   if (!initResponse.ok) {
      const payload = await initResponse
         .clone()
         .json()
         .catch(() => null);
      if (payload?.reason === "direct-upload-disabled") return "proxy";
      const failure = await readResponseFailure(initResponse);
      if (initResponse.status === HTTP_STATUS.PAYLOAD_TOO_LARGE) {
         return { error: "tooLarge" };
      }
      return failure;
   }

   const init = await initResponse.json();
   if (!init?.id || !init?.uploadUrl || typeof init.headers !== "object") {
      return null;
   }

   onProgress("uploading", init.id);
   const uploadResponse = await fetch(init.uploadUrl, {
      method: METHOD_TYPE.PUT,
      headers: init.headers,
      body: file
   });
   if (!uploadResponse.ok) return { error: "uploadFailed" };

   const completeUrl = api_url.chat_attachment_complete_url.replace(
      "{id}",
      encodeURIComponent(init.id)
   );
   const completeResponse = await fetchWithAuth(completeUrl, {
      method: METHOD_TYPE.POST
   });
   if (!completeResponse.ok) {
      return readResponseFailure(completeResponse);
   }

   onProgress("queued", init.id);
   return pollAttachmentStatus(init.id, onProgress, includeExtractedText);
};

export const uploadChatAttachment = async (
   file: File,
   onProgress: UploadProgress = () => undefined,
   includeExtractedText = false
): Promise<ChatAttachmentUploadResult | ChatAttachmentUploadFailure | null> => {
   try {
      const direct = await uploadDirect(file, onProgress, includeExtractedText);
      return direct === "proxy"
         ? uploadViaProxy(file, onProgress, includeExtractedText)
         : direct;
   } catch {
      return null;
   }
};
